import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "../../common/components/ExternalLink";
import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { OrganizationDataRecipientDto } from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import { getVendorsByCategory } from "../pages/get-compliant/data-recipients/shared";
import { Routes } from "../root/routes";
import { SurveyDefinition } from "./survey";
import { alphabetically, noneCheckboxOption, vendorToCheckboxOption } from "./util";
import { CONTRACTOR_CATEGORY, VENDOR_CATEGORY } from "../types/Vendor";
import { parseCheckboxSurveyAnswer } from "../util/surveyAnswerParsing";

const sharingInstructions = (): SurveyQuestionProps => {
  return {
    type: "acknowledgement",
    question: "What is targeted advertising?",
    slug: "sharing-definition-acknowledgement",
    text: (
      <>
        Consumers have the right to opt-out of targeted advertising (called "sharing" under the
        CCPA). Targeted advertising is advertising that is based on a person's online activity as it
        is tracked across different websites or devices, usually through an online identifier like a
        cookie or pixel placed on your website by an ad network like Facebook Ads.
      </>
    ),
  };
};

export const shareDataQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "share-data-for-advertising",
  question: "Does your website use tracking technology from ad networks for targeted advertising?",
  helpText:
    "For example, if your website visitors see advertisements for products they viewed on your site after they leave your site and visit other sites.",
});

export const CUSTOM_AUDIENCE_FEATURE_SLUG = "custom-audience-feature";
export const customAudienceFeatureQuestion = (
  vendors: OrganizationDataRecipientDto[],
  selectedSharingVendorAnswers: string,
): SurveyQuestionProps => {
  // Get all sharing vendor categories that were selected in the sharing categories question
  const sharingVendorCategories = [
    VENDOR_CATEGORY.AD_NETWORK,
    VENDOR_CATEGORY.SALES_MARKETING_TOOL,
    VENDOR_CATEGORY.DATA_ANALYTICS_PROVIDER,
    VENDOR_CATEGORY.DATA_BROKER,
    "Data Cooperative",
  ];

  const selectedSharingVendors = vendors.filter(
    (vendor) =>
      sharingVendorCategories.includes(vendor.category) &&
      vendor.collectionContext.includes("CONSUMER") &&
      selectedSharingVendorAnswers?.includes(vendor.id),
  );

  const sharingVendorOptions = selectedSharingVendors
    .sort(alphabetically)
    .map((vendor) => vendorToCheckboxOption(vendor));
  sharingVendorOptions.unshift(noneCheckboxOption());

  return {
    type: "checkboxes",
    slug: CUSTOM_AUDIENCE_FEATURE_SLUG,
    question:
      "Does your business upload a list of its customers or contacts to any of these vendors?",
    helpText: (
      <>
        Many vendors offer services like "
        <ExternalLink
          className="bold-link"
          href="https://www.facebook.com/business/help/170456843145568?id=2469097953376494"
        >
          Custom Audience
        </ExternalLink>
        " or "Customer Match," in which a business can upload a list of emails. The vendor will
        either deliver ads directly to those people and/or use it to create a "look-alike" audience.
        Regulators consider this to be targeted advertising, and thus subject to opt-outs.
        <br />
        <br />
        <b>Note:</b> If you upload emails indirectly via an integration with another vendor, please
        select any integrated vendors.
      </>
    ),
    options: sharingVendorOptions,
    noneOption: true,
  };
};

export const CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG = "custom-audience-feature-separate-vendor";
export const customAudienceSeparateVendorQuestion = (
  vendors: OrganizationDataRecipientDto[],
  vendorsRoute: string,
): SurveyQuestionProps => {
  const salesVendors = getVendorsByCategory(vendors, VENDOR_CATEGORY.SALES_MARKETING_TOOL);
  const salesContractors = getVendorsByCategory(vendors, CONTRACTOR_CATEGORY.SALES_MARKETING);
  const coMarketingPartners = getVendorsByCategory(vendors, VENDOR_CATEGORY.CO_MARKETING_PARTNER);
  const customAudienceManagedVendorsOptions = [
    ...salesVendors,
    ...salesContractors,
    ...coMarketingPartners,
  ]
    .sort(alphabetically)
    .map((sv) => vendorToCheckboxOption(sv));
  customAudienceManagedVendorsOptions.unshift(noneCheckboxOption());

  return {
    type: "checkboxes",
    slug: CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG,
    question:
      "Does your business manage custom audience lists for any ad networks through a separate vendor or platform? Select all vendors from the list below.",
    helpText: (
      <>
        For example, some businesses control audience lists through a marketing tool like Klaviyo or
        Listrak. The list below includes your Data Recipients categorized as Sales & Marketing
        Tools, Co-Marketing Partners, or Sales & Marketing Contractors. Don't see your platform
        manager in this list? Go to{" "}
        <Link className={"bold-link"} to={vendorsRoute}>
          Vendors
        </Link>{" "}
        to add it or to recategorize a custom vendor.
      </>
    ),
    options: customAudienceManagedVendorsOptions,
    visible: true,
  };
};

/**
 * Generates individual questions for each selected managing vendor to ask which ad networks they manage
 * These questions don't need survey slugs since data is stored directly in organization_managed_ad_network table
 */
export const generateCustomAudienceVendorManagedNetworkQuestions = (
  vendors: OrganizationDataRecipientDto[],
  selectedCustomAudienceFeatureAnswer: string,
  selectedVendorManagers: string,
): SurveyQuestionProps[] => {
  // Parse the selected sharing vendors from the custom audience feature question
  const selectedSharingVendorIds = parseCheckboxSurveyAnswer(selectedCustomAudienceFeatureAnswer);

  if (selectedSharingVendorIds.length === 0) {
    return [];
  }

  // Parse the selected vendor managers
  const selectedManagerIds = parseCheckboxSurveyAnswer(selectedVendorManagers);

  if (selectedManagerIds.length === 0) {
    return [];
  }

  // Get all sharing vendor categories
  const sharingVendorCategories = [
    VENDOR_CATEGORY.AD_NETWORK,
    VENDOR_CATEGORY.SALES_MARKETING_TOOL,
    VENDOR_CATEGORY.DATA_ANALYTICS_PROVIDER,
    VENDOR_CATEGORY.DATA_BROKER,
    "Data Cooperative",
  ];

  // Get the selected sharing vendors
  const selectedSharingVendors = vendors.filter(
    (vendor) =>
      sharingVendorCategories.includes(vendor.category) &&
      selectedSharingVendorIds.includes(vendor.id),
  );

  // Get the selected managing vendors
  const selectedManagers = vendors.filter((v) => selectedManagerIds.includes(v.id));

  const questions: SurveyQuestionProps[] = [];

  // Create a question for each managing vendor
  selectedManagers.forEach((manager) => {
    const sharingVendorOptions = selectedSharingVendors
      .sort(alphabetically)
      .map((vendor) => vendorToCheckboxOption(vendor));

    questions.push({
      type: "checkboxes",
      // Use a special prefix to indicate this is a non-persisted UI-only question
      slug: `__ui_only__custom-audience-managed-by-${manager.id}`,
      question: `Which vendor's custom audience lists are managed via ${
        manager.name || "this vendor"
      }?`,
      options: sharingVendorOptions,
      selectAllOption: false,
    });
  });

  return questions;
};

export const vendorSharingCategories = (vendors): SurveyQuestionProps => {
  // Get vendors from multiple categories that are considered sharing vendors
  const sharingVendorCategories = [
    VENDOR_CATEGORY.AD_NETWORK,
    VENDOR_CATEGORY.SALES_MARKETING_TOOL,
    VENDOR_CATEGORY.DATA_ANALYTICS_PROVIDER,
    VENDOR_CATEGORY.DATA_BROKER,
    "Data Cooperative", // Not in constants but exists in system
  ];

  const sharingVendors = vendors.filter(
    (vendor) =>
      sharingVendorCategories.includes(vendor.category) &&
      vendor.collectionContext.includes("CONSUMER"),
  );

  const sharingVendorOptions = sharingVendors
    .sort(alphabetically)
    .map((vendor) => vendorToCheckboxOption(vendor));

  return {
    type: "checkboxes",
    slug: "vendor-sharing-categories",
    question:
      "Which of these vendors does your business use for targeted advertising or data sharing?",
    helpText: (
      <>
        Listed below are vendors that you may be using for advertising, marketing, analytics, or
        data sharing purposes. This includes ad networks, sales & marketing tools, data analytics
        providers, data brokers, and data cooperatives. Something missing from this list? Go back to{" "}
        <Link className={"bold-link"} to={Routes.getCompliant.dataRecipients.root}>
          Vendors
        </Link>
        .
      </>
    ),
    emptyText:
      "You do not have any vendors categorized as sharing vendors (ad networks, sales & marketing tools, data analytics providers, data brokers, or data cooperatives).",
    options: sharingVendorOptions,
    noneOption: false,
  };
};

export const DIRECT_MAIL_VENDORS_SLUG = "direct-mail-vendors";
export const directMailVendorsQuestion = (
  vendors: OrganizationDataRecipientDto[],
): SurveyQuestionProps => {
  const directMailVendors = vendors.filter(
    (v) =>
      v.category == VENDOR_CATEGORY.SALES_MARKETING_TOOL ||
      v.category == VENDOR_CATEGORY.COMMUNICATION_TOOLS,
  );
  const directMailVendorsOptions = directMailVendors
    .sort(alphabetically)
    .map((adv) => vendorToCheckboxOption(adv));
  directMailVendorsOptions.unshift(noneCheckboxOption());

  return {
    type: "checkboxes",
    slug: DIRECT_MAIL_VENDORS_SLUG,
    question: "Does your business want to include direct mail vendors in its opt-out workflow?",
    helpText: (
      <>
        <p>
          Privacy opt-outs apply only to targeted online advertising, but many businesses choose to
          also remove consumers from promotional emails and mailers. Consumers may expect to be
          removed from such outreach when they exercise their opt-out right.
        </p>
        <p>
          Select vendors you'd like to bring into your opt-out workflow, or select "None" if you do
          not want to incorporate any direct mail vendors. These settings can be changed at any
          time.
        </p>
      </>
    ),
    options: directMailVendorsOptions,
    noneOption: true,
    visible: true,
  };
};

// POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
// export const facebookLduQuestion = (): SurveyQuestionProps => {
//   return {
//     type: "boolean",
//     slug: "facebook-ldu",
//     question: "Will your business enable Limited Data Use (LDU) for Facebook Ads?",
//     helpText: (
//       <>
//         You're not required to enable LDU. Enabling LDU will "turn off" personalized advertising via
//         Facebook Ads for all California visitors to your website. This may impact your advertising
//         for California web visitors, but it means California consumers will not need to click a link
//         on your website to opt-out of "sharing" with Facebook Ads.{" "}
//         <a
//           href="https://www.facebook.com/business/help/1151133471911882"
//           target="_blank"
//           rel="noreferrer noopener"
//         >
//           How to enable LDU
//         </a>
//         .
//       </>
//     ),
//   };
// };

// export const googleRdpQuestion = (): SurveyQuestionProps => {
//   return {
//     type: "boolean",
//     slug: "google-rdp",
//     question: "Will your business enable Restricted Data Processing (RDP) for Google Ads?",
//     helpText: (
//       <>
//         You're not required to enable RDP. Enabling RDP will "turn off" personalized advertising via
//         Google Ads for all California visitors to your website. This may impact your advertising for
//         California web visitors, but it means California consumers will not need to click a link on
//         your website to opt-out of "sharing" with Google Ads.{" "}
//         <a
//           href="https://support.google.com/google-ads/answer/9606827#zippy=%2Cedit-your-global-site-tag-to-disable-ad-personalization-signals"
//           target="_blank"
//           rel="noreferrer noopener"
//         >
//           How to enable RDP
//         </a>{" "}
//         (be sure to update your global site tag).
//       </>
//     ),
//   };
// };

const sharingQuestions = (
  _answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
): SurveyQuestionProps[] => {
  const baseQuestions = [
    sharingInstructions(), // "sharing-definition-acknowledgement"
    shareDataQuestion(), // "share-data-for-advertising"
    vendorSharingCategories(vendors), // "vendor-sharing-categories"
    // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
    // facebookLduQuestion(), // "facebook-ldu"
    // googleRdpQuestion(), // "google-rdp"
    customAudienceFeatureQuestion(vendors, _answers["vendor-sharing-categories"]), // "custom-audience-feature"
    customAudienceSeparateVendorQuestion(vendors, Routes.getCompliant.dataRecipients.root), // "custom-audience-feature-separate-vendor"
  ];

  // Add individual questions for each managing vendor
  const vendorNetworkQuestions = generateCustomAudienceVendorManagedNetworkQuestions(
    vendors,
    _answers["custom-audience-feature"],
    _answers["custom-audience-feature-separate-vendor"],
  );

  return [
    ...baseQuestions,
    ...vendorNetworkQuestions,
    directMailVendorsQuestion(vendors), // "direct-mail-vendors"
  ];
};

const sharingVisibility = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
  overrideAcknowledgement = false,
  hideDirectMailQuestion = false,
): Record<string, boolean> => {
  // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
  const rawSharingCategories = answers["vendor-sharing-categories"] || "[]";
  const sharingCategories: string[] = JSON.parse(rawSharingCategories) || [];
  // const sharingVendors = vendors.filter((v) => sharingCategories.includes(v.id));
  const answeredAcknowledgement =
    overrideAcknowledgement || Boolean(answers["sharing-definition-acknowledgement"]);

  const answeredSharesDataQuestion =
    !!answers["share-data-for-advertising"] && answers["share-data-for-advertising"].length > 0;
  const answeredVendorSharingCategories =
    answers["share-data-for-advertising"] == "false" || sharingCategories.length > 0;
  const answeredCustomAudienceFeature =
    !!answers[CUSTOM_AUDIENCE_FEATURE_SLUG] && answers[CUSTOM_AUDIENCE_FEATURE_SLUG] !== "[]";

  // Parse the checkbox arrays properly
  const customAudienceVendorIds: string[] = parseCheckboxSurveyAnswer(
    answers[CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG],
  );
  const answeredCustomAudienceVendorFeature = customAudienceVendorIds.length > 0;

  const usesCustomAudienceFeature =
    answeredCustomAudienceFeature && answers[CUSTOM_AUDIENCE_FEATURE_SLUG] !== '["none"]';
  const usesCustomAudienceVendorFeature =
    customAudienceVendorIds.length > 0 && !customAudienceVendorIds.includes("none");

  const baseVisibility = {
    "share-data-for-advertising": answeredAcknowledgement,
    "vendor-sharing-categories":
      answeredAcknowledgement && answers["share-data-for-advertising"] == "true",
    [CUSTOM_AUDIENCE_FEATURE_SLUG]:
      answeredAcknowledgement && answeredSharesDataQuestion && answeredVendorSharingCategories,
    // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
    // "facebook-ldu":
    //   answers["share-data-for-advertising"] == "true" && Boolean(findFacebookAds(sharingVendors)),
    // "google-rdp":
    //   answers["share-data-for-advertising"] == "true" && Boolean(findGoogleAds(sharingVendors)),
    [CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG]:
      answeredAcknowledgement &&
      answeredSharesDataQuestion &&
      answeredVendorSharingCategories &&
      usesCustomAudienceFeature,
    [DIRECT_MAIL_VENDORS_SLUG]:
      !hideDirectMailQuestion &&
      answeredAcknowledgement &&
      answeredSharesDataQuestion &&
      answeredVendorSharingCategories &&
      answeredCustomAudienceFeature &&
      (!usesCustomAudienceFeature || answeredCustomAudienceVendorFeature),
  };

  // Add visibility for individual vendor network questions (UI-only)
  if (usesCustomAudienceVendorFeature) {
    customAudienceVendorIds.forEach((managerId) => {
      if (managerId !== "none") {
        baseVisibility[`__ui_only__custom-audience-managed-by-${managerId}`] = true;
      }
    });
  }

  return baseVisibility;
};

export const sharingSurvey = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
  overrideAcknowledgement = false,
  hideDirectMailQuestion = false, // In POL-6573 we moved the direct mail question fully to stay compliant, so this param is a temporary workaround to hide that question from get compliant (until get compliant is fully deprecated)
): SurveyDefinition => {
  return {
    questions: sharingQuestions(answers, vendors),
    visibility: sharingVisibility(
      answers,
      vendors,
      overrideAcknowledgement,
      hideDirectMailQuestion,
    ),
    disabled: {},
    relatedQuestionGroups: [[DIRECT_MAIL_VENDORS_SLUG]],
  };
};

export const CUSTOM_AUDIENCE_VENDOR_MANAGED_SHARING_VENDORS_FEATURE_SLUG =
  "custom-audience-feature-separate-vendor-managed-sharing-vendors";

/**
 * Aggregates all the individual vendor managed sharing vendor answers into a single array
 * for backward compatibility with existing code that expects the old format
 */
export const getAggregatedVendorManagedSharingVendors = (answers: SurveyAnswers): string[] => {
  const aggregatedVendors: string[] = [];

  // Find all answers that start with "__ui_only__custom-audience-managed-by-"
  Object.keys(answers).forEach((slug) => {
    if (slug.startsWith("__ui_only__custom-audience-managed-by-")) {
      const vendorIds = parseCheckboxSurveyAnswer(answers[slug]);
      vendorIds.forEach((vendorId) => {
        if (!aggregatedVendors.includes(vendorId)) {
          aggregatedVendors.push(vendorId);
        }
      });
    }
  });

  return aggregatedVendors;
};

/**
 * Gets vendor managed sharing vendors in the old format for backward compatibility
 * Returns a JSON string array like the old question used to
 *
 * @deprecated This function name is kept for backward compatibility.
 * Use getAggregatedVendorManagedSharingVendors for new code.
 */
export const getVendorManagedAdNetworksIds = (answers: SurveyAnswers): string => {
  const vendorIds = getAggregatedVendorManagedSharingVendors(answers);
  return JSON.stringify(vendorIds);
};
